import type { ICharacter, UpdateCharacterType } from '@/types';

// The merge sequence behind injected deps so the orchestration is unit-testable
// without a query client. useMergeCharacters wires these to the real endpoints.
export interface MergeCharacterDeps {
    fetchCharacter: (characterId: number) => Promise<ICharacter>;
    updateCharacter: (characterId: number, data: Partial<UpdateCharacterType>) => Promise<unknown>;
    deleteCharacter: (characterId: number) => Promise<unknown>;
    /** characterExtras.absorbExtras bound to the project. */
    absorbExtras: (targetId: number, sourceId: number) => void;
}

/** Real scalar fields the merge fills on the target when the target's slot is empty. */
const FILLABLE_FIELDS = ['full_name', 'alias', 'age_category', 'role', 'description', 'initial_goal', 'final_goal'] as const;

/**
 * «Partial real merge» (mock phase): the target absorbs the source — the
 * source fills only the target's empty fields, extras merge target-empty-wins,
 * then the source is deleted. Scene-mention retagging AND photo transfer are
 * the backend's part of the frozen contract and do NOT happen here (probed
 * live 2026-08-10: `PUT assets/{id}` answers success but silently ignores
 * `assetable_id`, so re-parenting is impossible client-side) — the merge
 * dialog says so.
 */
export async function mergeCharacters(deps: MergeCharacterDeps, targetId: number, sourceId: number) {
    const [target, source] = await Promise.all([deps.fetchCharacter(targetId), deps.fetchCharacter(sourceId)]);

    const fill: Record<string, unknown> = {};
    for (const key of FILLABLE_FIELDS) {
        if (!target[key] && source[key]) fill[key] = source[key];
    }
    if (Object.keys(fill).length > 0) {
        await deps.updateCharacter(targetId, fill as Partial<UpdateCharacterType>);
    }

    deps.absorbExtras(targetId, sourceId);
    await deps.deleteCharacter(sourceId);
}
